import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { PageOpensDay, PageOpensReport } from '@/types/leaderboards';
import { countWithShare, formatAway, notBack, totals } from './pageOpens';

const { Title, Text, Paragraph } = Typography;

const DAY_OPTIONS = [7, 14, 30, 60].map((d) => ({ value: d, label: `Last ${d} days` }));

const columns: ColumnsType<PageOpensDay> = [
  { title: 'Day (IST)', dataIndex: 'day', key: 'day', fixed: 'left' },
  { title: 'Page opens', dataIndex: 'opens', key: 'opens', align: 'right' },
  { title: 'Visits', dataIndex: 'visits', key: 'visits', align: 'right' },
  { title: 'Players', dataIndex: 'players', key: 'players', align: 'right' },
  {
    title: 'Back in 5 min',
    key: 'back5m',
    align: 'right',
    render: (_, d) => countWithShare(d.back5m, d.visits),
  },
  {
    title: 'Back in 30 min',
    key: 'back30m',
    align: 'right',
    render: (_, d) => countWithShare(d.back30m, d.visits),
  },
  {
    title: 'Back in 24 h',
    key: 'back24h',
    align: 'right',
    render: (_, d) => countWithShare(d.back24h, d.visits),
  },
  {
    title: 'Not back',
    key: 'notBack',
    align: 'right',
    render: (_, d) => countWithShare(notBack(d), d.visits),
  },
  {
    title: 'Still waiting',
    dataIndex: 'pending',
    key: 'pending',
    align: 'right',
  },
  {
    title: 'Median time away',
    key: 'medianAwaySec',
    align: 'right',
    render: (_, d) => formatAway(d.medianAwaySec),
  },
  {
    title: 'Match in 1 h',
    key: 'played1h',
    align: 'right',
    render: (_, d) => countWithShare(d.played1h, d.mature1h),
  },
];

/**
 * Leaderboard page opened from the game's custom ad, and whether the player came back to the app.
 * Backend: GET /admin/leaderboards/page-opens (analytics:list).
 */
export const LeaderboardOpensPage: React.FC = () => {
  const [days, setDays] = useState(14);
  const [report, setReport] = useState<PageOpensReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.getLeaderboardPageOpens(days);
      setReport(data as PageOpensReport);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the report');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    void load();
  }, [load]);

  const sum = useMemo(() => totals(report?.days ?? []), [report]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Title level={3} style={{ margin: 0 }}>
          Leaderboard page opens
        </Title>
        <Space>
          <Select value={days} options={DAY_OPTIONS} onChange={setDays} style={{ width: 150 }} />
          <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
            Reload
          </Button>
        </Space>
      </Space>

      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Players who tapped the leaderboard banner in the game and opened the page with their own
        league. A visit is a player&apos;s page opens with no gap over 30 minutes. &quot;Back&quot;
        means the player started a new app session after the visit (the app reports one each time it
        comes to the foreground). &quot;Still waiting&quot; are visits under 24 h old that have not
        come back yet. &quot;Match in 1 h&quot; counts real matches started within an hour of the
        visit; its share is over the visits at least an hour old. Page opens count at most one per
        player per 10 s. Recorded since the backend release that added this report; taps that fell
        back to the public board (expired token) are not counted.
      </Paragraph>

      {error && (
        <Alert type="error" showIcon message="Could not load the report" description={error} />
      )}

      <Card size="small">
        <Space size="large" wrap>
          <Text>
            Visits: <Text strong>{sum.visits}</Text>
          </Text>
          <Text>
            Back in 30 min: <Text strong>{countWithShare(sum.back30m, sum.visits)}</Text>
          </Text>
          <Text>
            Back in 24 h: <Text strong>{countWithShare(sum.back24h, sum.visits)}</Text>
          </Text>
          <Text>
            Match in 1 h: <Text strong>{countWithShare(sum.played1h, sum.mature1h)}</Text>
          </Text>
          <Text type="secondary">Page opens: {sum.opens}</Text>
        </Space>
      </Card>

      <Table<PageOpensDay>
        rowKey="day"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={report?.days ?? []}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'No page opens from the game in this range yet' }}
      />
    </Space>
  );
};

export default LeaderboardOpensPage;
